import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

import { useCurrentUser } from "../api/useCurrentUser";
import "./AppNav.css";

/**
 * Secondary navigation bar that sits flush below the app header on
 * every authenticated route. Two visual groups:
 *
 *   Primary nav (left):
 *     - Home (/)
 *     - Whānau (/directory)
 *     - Manager settings (/manager) — manager role and above
 *     - Admin settings (/admin) — administrator role and above
 *
 *   Secondary actions (right):
 *     - Language toggle (Te Reo Māori / English)
 *     - Sign out
 *
 * Lang toggle and sign-out used to live in the header. They moved
 * here so the header is just brand + greeting, and the nav row
 * owns every page-level action a kaimahi might take.
 *
 * NavLink provides the .active class for primary links. Action
 * buttons are styled to match the nav links visually but are not
 * NavLinks — they don't represent a route.
 */
export function AppNav() {
  const { t, i18n } = useTranslation();
  const { instance } = useMsal();
  const currentUser = useCurrentUser();

  const roleId =
    currentUser.kind === "ok" ? currentUser.data.role.roleId : null;

  const showManagerSettings =
    roleId !== null &&
    ["manager", "exec-manager", "administrator", "super-admin"].includes(
      roleId,
    );

  const showAdminSettings =
    roleId !== null && ["administrator", "super-admin"].includes(roleId);

  // Reporting visibility follows the reports.view permission (super-admin by
  // default; an admin can grant other roles via /admin/permissions). Falls back
  // to super-admin if the API hasn't surfaced permissions yet.
  const permissions =
    currentUser.kind === "ok" ? currentUser.data.permissions : undefined;
  const showReports = permissions?.["reports.view"] ?? roleId === "super-admin";

  const toggleLanguage = () => {
    const next = i18n.language === "mi" ? "en" : "mi";
    void i18n.changeLanguage(next);
    document.documentElement.lang = next;
  };

  const handleSignOut = () => {
    const account = instance.getActiveAccount() ?? undefined;
    instance
      .logoutPopup({
        account,
        mainWindowRedirectUri: window.location.origin,
      })
      .catch((error: unknown) => {
        console.error("[WFP] Sign-out failed:", error);
      });
  };

  return (
    <nav className="app-nav" aria-label={t("nav.label")}>
      <div className="app-nav__primary">
        <NavLink to="/" end className="app-nav__link">
          {t("nav.home")}
        </NavLink>
        <NavLink to="/directory" className="app-nav__link">
          {t("nav.whanau")}
        </NavLink>
        {showManagerSettings && (
          <NavLink to="/manager" className="app-nav__link">
            {t("nav.managerSettings")}
          </NavLink>
        )}
        {showAdminSettings && (
          <NavLink to="/admin" className="app-nav__link">
            {t("nav.adminSettings")}
          </NavLink>
        )}
        {showReports && (
          <NavLink to="/reports" className="app-nav__link">
            {t("nav.reports")}
          </NavLink>
        )}
      </div>
      <div className="app-nav__secondary">
        <button
          type="button"
          className="app-nav__link app-nav__link--action"
          onClick={toggleLanguage}
          aria-label={t("app.toggleLanguage")}
        >
          {i18n.language === "mi" ? "English" : "Te Reo Māori"}
        </button>
        <button
          type="button"
          className="app-nav__link app-nav__link--action"
          onClick={handleSignOut}
        >
          {t("auth.signOut")}
        </button>
      </div>
    </nav>
  );
}

export default AppNav;
